import { AddCircle, ArrowForward } from '@mui/icons-material';
import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import {
    capitalizeName,
    formatServiceItemsList,
} from '../../../../../shared/utils';
import { ServiceType } from '../../../../enums/contractor';
import { FormValues as Experience } from '../../../../types/experience';
import { serviceTypeLeadingIcon } from '../../../Contractors/contractorServiceIcons';
import { AvailableOptionalService } from '../../EditReservation/optionalServiceAvailability';

function getOptionalServiceDescription(
    serviceType: ServiceType,
    items: string[],
): string {
    switch (serviceType) {
        case ServiceType.Catering:
            return 'Arrange catering for your booking with preferred date, time, menu, or dietary requirements.';
        case ServiceType.Wellness:
            return 'Book wellness & lifestyle services with preferred therapist, time, and treatment.';
        case ServiceType.Workshop:
            return 'Book a workshop and add requests per provider, time, and session.';
        case ServiceType.Alcohol:
            return 'Request beverages, bar service, or drinks packages for your booking.';
        case ServiceType.Other:
            return `Request ${formatServiceItemsList(items)} for your booking.`;
        default:
            return '';
    }
}

const OptionalServiceList = ({
    experience,
    onSelect,
}: {
    experience: Experience;
    onSelect: (service: AvailableOptionalService) => void;
}) => {
    const servicesByType = new Map<ServiceType, string[]>();

    for (const contractor of experience.contractors) {
        const { type, items } = contractor.services;

        if (type === ServiceType.Cleaning) {
            continue;
        }

        const existingItems = servicesByType.get(type) ?? [];
        servicesByType.set(type, [...existingItems, ...items]);
    }

    for (const setMenu of experience.setMenus ?? []) {
        const { serviceType } = setMenu;

        if (serviceType === ServiceType.Cleaning) {
            continue;
        }

        const existingItems = servicesByType.get(serviceType) ?? [];
        servicesByType.set(serviceType, [...existingItems, setMenu.name]);
    }

    const availableServices: AvailableOptionalService[] = Array.from(
        servicesByType.entries(),
    ).map(([serviceType, items]) => ({
        serviceType,
        description: getOptionalServiceDescription(
            serviceType,
            Array.from(new Set(items)),
        ),
    }));

    return (
        <List disablePadding>
            {availableServices.map((service, index) => {
                const Icon = serviceTypeLeadingIcon(service.serviceType);

                return (
                    <ListItem
                        key={service.serviceType}
                        disableGutters
                        divider={index !== availableServices.length - 1}
                    >
                        <ListItemAvatar sx={{ pr: 1 }}>
                            <Box
                                sx={{
                                    width: 75,
                                    height: 75,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.12),
                                    color: 'primary.main',
                                }}
                            >
                                <Icon sx={{ fontSize: 32 }} />
                            </Box>
                        </ListItemAvatar>
                        <ListItemText
                            primary={capitalizeName(service.serviceType)}
                            secondary={service.description}
                            primaryTypographyProps={{
                                sx: { fontWeight: 'bold' },
                            }}
                            secondaryTypographyProps={{
                                sx: {
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                },
                            }}
                        />
                        <ListItemSecondaryAction>
                            <IconButton
                                color='primary'
                                aria-label={`Request ${service.serviceType}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(service);
                                }}
                            >
                                <AddCircle />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                );
            })}
        </List>
    );
};

export default OptionalServiceList;
